import axios from "axios";

import { Property } from "manager/interfaces/api";

import ROUTES from "shared/config/apiRoutes";
import { transformRoute } from "shared/utils/routing";

import { API_URL } from "./setupApi";

export const getProperties = (params) => {
  return axios.get(`${API_URL}${ROUTES.properties}`, {
    params: {
      id: params?.id,
      portfolio_id: params?.portfolioId,
      name_or_address: params?.nameOrAddress,
      portfolio__is_default: params?.hasPortfolio,
      page: params?.page,
      page_size: params?.pageSize,
      // Only holders send this. The API ignores it without 2987; omit so
      // customer list calls stay identical to the pre-billing query string.
      ...(params?.billingStatus
        ? { billing_status: params.billingStatus }
        : {}),
    },
  });
};

// TODO (POD2-148): Remove the Perf postfix as soon as we have removed the old endpoints.
export const getPropertiesPerf = (params) => {
  return axios
    .get(`${API_URL}${ROUTES.propertiesPerf}`, {
      params: {
        id: params?.id,
        portfolio_id: params?.portfolioId,
        name_or_address: params?.nameOrAddress,
        portfolio__is_default: params?.hasPortfolio,
        page: params?.page,
        page_size: params?.pageSize,
        // Holders only. Same as getProperties — API ignores without 2987.
        ...(params?.billingStatus
          ? { billing_status: params.billingStatus }
          : {}),
      },
    })
    .then((response) => response.data);
};

export const getAllPropertiesSimple = () => {
  return axios.get(`${API_URL}${ROUTES.allPropertiesSimple}`);
};

export const getPropertiesWithListing = (params) =>
  axios.get(`${API_URL}${ROUTES.propertiesWithUnitListing}`, {
    params: {
      portfolio_id: params?.portfolioId,
      name_or_address: params?.nameOrAddress,
      portfolio__is_default: params?.hasPortfolio,
      with_units: params?.withUnits,
      deal: params?.deal,
    },
  });

export const getPropertiesWithAlerts = () =>
  axios
    .get(`${API_URL}${ROUTES.properties}`, {
      params: {
        with_alerts: true,
      },
    })
    .then((response) => response.data);

export const assignPropertyToPortfolio = ({ propertyId, portfolioId }) =>
  axios.post(
    `${API_URL}${transformRoute(ROUTES.assignPropertyToPortfolio, {
      propertyId,
    })}`,
    {
      portfolio_id: portfolioId,
    }
  );

export const unassignPropertyFromPortfolio = (propertyId) =>
  axios.post(
    `${API_URL}${transformRoute(ROUTES.unassignPropertyFromPortfolio, {
      propertyId,
    })}`
  );

export const getProperty = (propertyId) =>
  axios.get(
    `${API_URL}${transformRoute(ROUTES.property, {
      propertyId,
    })}`
  );

export const createProperty = ({
  address,
  city,
  propertyType,
  state,
  zipcode,
  name,
}) =>
  axios
    .post(`${API_URL}${ROUTES.properties}`, {
      address,
      city,
      propertyType,
      state,
      zipcode,
      name,
    })
    .then(({ data }) => data);

export const updateProperty = (id: number, property: Property) =>
  axios
    .patch(
      `${API_URL}${transformRoute(ROUTES.property, { propertyId: id })}`,
      property
    )
    .then(({ data }) => data);

export const assignEmployeesToProperty = ({ id, employees }) =>
  axios.post(
    `${API_URL}${transformRoute(ROUTES.assignEmployees, { propertyId: id })}`,
    employees
  );

export const getEmployeesAssignedToProperty = ({ id }) =>
  axios.get(
    `${API_URL}${transformRoute(ROUTES.listEmployees, {
      propertyId: id,
    })}`
  );

// TODO (POD2-148): Remove the Perf postfix as soon as we have removed the old endpoints.
export const getEmployeesAssignedToPropertyPerf = ({ id }) =>
  axios
    .get(
      `${API_URL}${transformRoute(ROUTES.listEmployeesPerf, {
        propertyId: id,
      })}`
    )
    .then(({ data }) => data);

export const getEmployeesWithAssignmentsOnProperty = ({
  id,
  // @ts-ignore
  filters: { onlyYardiUnmapped, excludeUnassigned } = {},
}) =>
  axios.get(
    `${API_URL}${transformRoute(ROUTES.listEmployeesWithAssignments, {
      propertyId: id,
    })}`,
    {
      params: {
        only_yardi_unmapped: onlyYardiUnmapped,
        exclude_unassigned: excludeUnassigned,
      },
    }
  );

export const uploadPropertyLogo = (id, file) => {
  const formData = new FormData();
  formData.append("logo", file);
  return axios.post(
    `${API_URL}${transformRoute(ROUTES.uploadPropertyLogo, {
      propertyId: id,
    })}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
};

export const togglePropertyVeroGo = ({
  propertyId,
  active,
}: {
  propertyId: number;
  active: boolean;
}) =>
  axios
    .patch(
      `${API_URL}${transformRoute(ROUTES.property, {
        propertyId,
      })}toggle-vero-go-active/`,
      { active }
    )
    .then(({ data }) => data);

export const resetPropertyVeroGoToPortfolioSettings = ({
  propertyId,
}: {
  propertyId: number;
}) =>
  axios
    .post(
      `${API_URL}${transformRoute(ROUTES.resetVeroGoToPortfolioSettings, {
        propertyId,
      })}`
    )
    .then(({ data }) => data);

export const getPropertiesWithInvitation = () =>
  axios
    .get(`${API_URL}${ROUTES.propertiesWithInvitation}`)
    .then((response) => response.data);

export const getPropertiesWithLead = () =>
  axios
    .get(`${API_URL}${ROUTES.propertiesWithLead}`)
    .then((response) => response.data);

const propertyBillingPath = (route, propertyId) =>
  `${API_URL}${transformRoute(route, { propertyId })}`;

export const goLivePropertyBilling = (propertyId, payload) =>
  axios
    .post(
      propertyBillingPath(ROUTES.propertyBillingGoLive, propertyId),
      payload
    )
    .then(({ data }) => data);

export const changePropertyGoLive = (propertyId, payload) =>
  axios
    .post(
      propertyBillingPath(ROUTES.propertyBillingChangeGoLive, propertyId),
      payload
    )
    .then(({ data }) => data);

export const stopPropertyBilling = (propertyId, payload) =>
  axios
    .post(propertyBillingPath(ROUTES.propertyBillingStop, propertyId), payload)
    .then(({ data }) => data);

export const cancelScheduledPropertyBilling = (propertyId, payload) =>
  axios
    .post(
      propertyBillingPath(ROUTES.propertyBillingCancelScheduled, propertyId),
      payload
    )
    .then(({ data }) => data);

export const setPropertyBillableUnits = (propertyId, payload) =>
  axios
    .post(propertyBillingPath(ROUTES.propertyBillingUnits, propertyId), payload)
    .then(({ data }) => data);

export const getPropertyBillingHistory = (
  propertyId,
  params: { page?: number; pageSize?: number } = {}
) =>
  axios
    .get(propertyBillingPath(ROUTES.propertyBillingHistory, propertyId), {
      params: {
        page: params.page,
        page_size: params.pageSize,
      },
    })
    .then(({ data }) => data);

export const bulkGoLivePropertyBilling = (payload) =>
  axios
    .post(`${API_URL}${ROUTES.propertyBillingBulkGoLive}`, payload)
    .then(({ data }) => data);
